// src/components/Dashboard/RoleDashboard/RoleExtras/Notifications.jsx
import React, { useEffect, useState, useRef } from "react";
import { BASE_URL } from "../../../../config";
import { apiFetch } from "../../../../apiFetch";
import io from "socket.io-client";
import { useLocation, useParams } from "react-router-dom";
import { resolveId } from "../roleApi";
import "./Notifications.css";

const toFrontendLink = (maybePathOrUrl) => {
    if (!maybePathOrUrl) return "#";
    if (/^https?:\/\//i.test(maybePathOrUrl)) return maybePathOrUrl;
    const path = maybePathOrUrl.startsWith("/") ? maybePathOrUrl : `/${maybePathOrUrl}`;
    return `${window.location.origin}${path}`;
};

const VALID_ROLES = ["player", "agent", "scout", "club"];
const isDev =
    typeof process !== "undefined"
        ? process.env.NODE_ENV !== "production"
        : true;

/* ===========================
   🔑 ROLE + ID RESOLUTION
=========================== */
function useRoleAndId(props) {
    const params = useParams();

    let role =
        (
            props?.role ||
            params.role ||
            (typeof window !== "undefined"
                ? localStorage.getItem("role") || localStorage.getItem("userRole")
                : "") ||
            "agent"
        ).toLowerCase();

    if (!VALID_ROLES.includes(role)) role = "agent";

    const rawId = props?.id || params.id || params.userId;

    const id = resolveId({
        role,
        id: rawId,
        agentId: props?.agentId,
        scoutId: props?.scoutId,
        clubId: props?.clubId,
        userId: props?.userId,
    });

    if (isDev) {
        console.debug("[Notifications] role/id", { role, rawId, resolved: id });
    }

    return { role, id };
}

/* ===========================
   📬 NOTIFICATIONS PAGE
=========================== */
export default function Notifications(props) {
    const { role, id } = useRoleAndId(props);

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedNotification, setSelectedNotification] = useState(null);

    const location = useLocation();
    const clickedFromRoute = location.state?.notification || null;
    const listRef = useRef(null);
    const socketRef = useRef(null);

    const roleLabel = role
        ? role.charAt(0).toUpperCase() + role.slice(1)
        : "User";

    const activeNotification =
        selectedNotification || clickedFromRoute || null;

    /* ===========================
       📡 FETCH NOTIFICATIONS
    =========================== */
    useEffect(() => {
        if (!id) return;

        const fetchNotifications = async () => {
            setLoading(true);
            setError("");

            try {
                const res = await apiFetch(`/api/notifications/${role}/${id}`);

                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(text || `Failed to load notifications (${res.status})`);
                }

                const data = await res.json();

                const arr = Array.isArray(data)
                    ? data
                    : data?.items || data?.data || data?.notifications || [];

                const sorted = arr
                    .slice()
                    .sort(
                        (a, b) =>
                            new Date(b.timestamp) - new Date(a.timestamp)
                    );

                setNotifications(sorted);
            } catch (err) {
                console.error("❌ Error fetching notifications:", err);
                setError("Failed to load notifications.");
            } finally {
                setLoading(false);
            }
        };

        fetchNotifications();
    }, [id, role]);

    /* ===========================
       🔔 REAL-TIME NOTIFICATIONS
    =========================== */
    useEffect(() => {
        if (!id || socketRef.current) return;

        socketRef.current = io(BASE_URL);
        socketRef.current.emit("registerUser", { userId: id, role });

        socketRef.current.on("newNotification", (notification) => {
            // Support both camelCase and snake_case from server
            const targetId = notification.userId ?? notification.user_id;

            // Never treat missing targetId as broadcast
            const sameUser =
                targetId !== undefined && targetId !== null
                    ? String(targetId) === String(id)
                    : false;

            const sameRole =
                !notification.role ||
                String(notification.role).toLowerCase() === String(role).toLowerCase();

            if (!sameUser || !sameRole) return;

            setNotifications((prev) => [{ ...notification, isRead: false }, ...prev]);
        });

        return () => {
            socketRef.current?.disconnect();
            socketRef.current = null;
        };
    }, [id, role]);

    /* ===========================
       🎯 SEED FROM ROUTE
    =========================== */
    useEffect(() => {
        if (clickedFromRoute) {
            setSelectedNotification(clickedFromRoute);
        }
    }, [clickedFromRoute]);

    /* ===========================
       📍 SCROLL TO ACTIVE
    =========================== */
    useEffect(() => {
        if (activeNotification && listRef.current) {
            const el = document.getElementById(
                `notification-${activeNotification.id}`
            );
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }, [notifications, activeNotification]);

    const handleNotificationClick = async (note) => {
        setSelectedNotification(note);

        // optimistic UI update
        setNotifications((prev) =>
            prev.map((n) =>
                n.id === note.id ? { ...n, isRead: true } : n
            )
        );

        try {
            await apiFetch(`/api/notifications/${role}/${id}/read`, {
                method: "PUT",
                // optional if your backend supports per-note marking:
                // body: JSON.stringify({ notificationId: note.id }),
            });
        } catch (err) {
            console.error("Failed to mark notification as read:", err);
        }
    };

    /* ===========================
       🧱 RENDER
    =========================== */
    return (
        <div className="role-notifications">
            {/* ✅ Removed inbox-layout so the page can truly fill full width */}
            <div className="notifications-page">
                <h2>{roleLabel} Notifications</h2>
                <p className="notifications-subtitle">
                    (Important Notifications Will Show Here)
                </p>

                {loading && (
                    <p className="notifications-status">
                        Loading notifications...
                    </p>
                )}

                {error && (
                    <p className="notifications-status error">
                        {error}
                    </p>
                )}

                <ul className="notifications-list inbox" ref={listRef}>
                    {notifications.map((note) => {
                        const isUnread = !note.isRead;
                        const isSelected =
                            activeNotification?.id === note.id;

                        return (
                            <li
                                key={note.id}
                                id={`notification-${note.id}`}
                                className={`notification-row ${isUnread ? "unread" : "read"
                                    } ${isSelected ? "selected" : ""}`}
                                onClick={() => handleNotificationClick(note)}
                            >
                                <div className="row-left">
                                    {isUnread && <span className="unread-dot" />}
                                </div>

                                <div className="row-content">
                                    <div className="row-title">
                                        {note.type || "Contract Update"}
                                    </div>

                                    <div className="row-message">
                                        {note.message}
                                    </div>

                                    {note.link && (
                                        <a
                                            href={toFrontendLink(note.link)}
                                            className="notification-link"
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }
                                        >
                                            {note.linkLabel || "View"}
                                        </a>
                                    )}
                                </div>

                                <div className="row-time">
                                    {new Date(note.timestamp).toLocaleString()}
                                </div>
                            </li>
                        );
                    })}

                    {!loading && !error && notifications.length === 0 && (
                        <li className="notification-empty">
                            No notifications yet.
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
}








